import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button.js';
import { useAuthStore } from '../stores/auth.js';
import { Hero } from '../components/lobby/Hero.js';
import { HeroCarousel } from '../components/lobby/HeroCarousel.js';
import { HotGamesGrid, HotGamesSkeleton } from '../components/lobby/HotGamesGrid.js';
import { useHotGames } from '../features/games/useHotGames.js';
import { PromoRowCard } from '../components/lobby/PromoRowCard.js';
import { useDragScroll } from '../components/lobby/useDragScroll.js';
import { useOriginalsCatalog, withOriginalsCatalog } from '../features/originals/useOriginalsCatalog.js';
import { fetchPromotions, PROMO_QUERY_KEYS } from '../features/promo/api.js';
import { ORIGINALS } from '../features/originals/catalog.js';
import { originalName } from '../features/originals/names.js';
import { SiteFooter } from '../components/site/SiteFooter.js';
import { AnnouncementsBanner } from '../features/announcements/AnnouncementsBanner.js';
import type { GameLike } from '../components/lobby/GameCard.js';

export const Route = createFileRoute('/_player/')({
  component: LobbyPage,
});

// ── Promotions row (section 3) ───────────────────────────────────────────────
// Horizontal shopfront of active platform promotions. Public data (guest-safe);
// self-hides when there are none. Tapping a card links into the /promos page
// where claiming happens.
function PromotionsRow({ isAuthed }: { isAuthed: boolean }) {
  const { t } = useTranslation('lobby');
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: PROMO_QUERY_KEYS.list({ status: 'active' }),
    queryFn: () => fetchPromotions({ status: 'active' }),
    staleTime: 60_000,
  });

  const items = q.data?.items ?? [];
  const scrollerRef = useDragScroll<HTMLDivElement>();
  if (!q.isPending && items.length === 0) return null;

  const go = () => void navigate({ to: isAuthed ? '/promos' : '/login' });

  function scrollBy(dir: number) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  }

  return (
    <section className="flex flex-col gap-3 w-full">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[18px] md:text-[22px] font-bold text-[var(--text-primary)]">
          {t('landing.promotion.title')}
        </h2>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="hidden md:grid w-8 h-8 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] place-items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="hidden md:grid w-8 h-8 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] place-items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
          <Link to="/promos" className="ml-1 text-[14px] font-semibold text-[var(--text-primary)] hover:text-[var(--accent-strong)] transition-colors">
            {t('landing.promotion.seeAll')}
          </Link>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-2 bf-noscroll snap-x snap-mandatory select-none cursor-grab"
        style={{ scrollPaddingLeft: '1rem', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {q.isPending
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="shrink-0 w-[280px] h-[264px] rounded-2xl bg-[var(--bg-elevated)]/50 animate-pulse" />
            ))
          : items.map((p) => <PromoRowCard key={p.id} promo={p} onClick={go} />)}
      </div>
    </section>
  );
}

// ── Popular games (section 2) ────────────────────────────────────────────────
// Slots ranked by their real RTP over the last 24h of play on this brand
// (svc-bet), re-ranked each minute. Hidden when the brand has no slots.
function LobbyContent() {
  const { t } = useTranslation(['lobby', 'common']);
  const { games, isPending, isError } = useHotGames();
  if (isError || (!isPending && games.length === 0)) return null;

  return (
    <section className="flex flex-col gap-5 w-full">
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <h2 className="bf-display text-[28px] md:text-[40px] leading-none text-[var(--text-primary)] drop-shadow-[0_2px_10px_var(--primary-glow)]">
            {t('lobby:hotGames.title')}
          </h2>
          <p className="text-[12px] md:text-[13px] text-[var(--text-secondary)]">{t('lobby:hotGames.hint')}</p>
        </div>
        <Link
          to="/casino"
          search={{ gameType: 'slot' }}
          className="shrink-0 text-[14px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          {t('common:common.viewAll', { defaultValue: 'ดูทั้งหมด' })} →
        </Link>
      </div>

      {isPending ? <HotGamesSkeleton /> : <HotGamesGrid games={games} />}

      <div className="flex justify-center pt-2">
        <Button asChild variant="secondary" size="lg" className="px-8">
          <Link to="/casino">
            {t('common:common.viewAll', { defaultValue: 'ดูทั้งหมด' })}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}

// ── Route component ──────────────────────────────────────────────────────────

// Hero wheel — every in-house Original, driven by the canonical catalog so it
// always matches reality (and the TopBar dropdown). Cards carry the game `code`;
// the carousel resolves their art and play navigates straight to the game.
const HERO_GAMES: GameLike[] = ORIGINALS.map((o) => ({
  id: `originals:${o.code}`,
  name: o.name,
  code: o.code,
  gameType: o.code,
  providerDisplayName: 'Originals',
}));

function LobbyPage() {
  const { t } = useTranslation();
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const navigate = useNavigate();

  // Originals' RTP and any card art uploaded in BO → Games come from the catalog,
  // matched by code — the same source as the casino and /games/originals grids,
  // so the wheel can never disagree with them.
  const originalsCatalog = useOriginalsCatalog();
  const heroGames: GameLike[] = withOriginalsCatalog(
    HERO_GAMES.map((g) => ({ ...g, name: originalName(g.code!, t), providerCode: 'originals' })),
    originalsCatalog,
  );

  // Open for a visitor too: the game page renders without a session and asks
  // for a sign-in only at the bet.
  function onHeroPlay(game: GameLike) {
    if (!game.code) return;
    void navigate({ to: '/games/originals/$code', params: { code: game.code } });
  }

  return (
    <div className="flex flex-col gap-12">
      <Hero>
        <HeroCarousel games={heroGames} onPlay={onHeroPlay} />
      </Hero>
      <AnnouncementsBanner />
      <LobbyContent />
      <PromotionsRow isAuthed={isAuthed} />
      <SiteFooter />
    </div>
  );
}
